import { StarIcon } from '@heroicons/react/24/solid';
import type { SurveyQuestion } from '../../services/customerSuccessEngagementApi';

type SurveyQuestionTypePickerProps = {
  value: SurveyQuestion['type'];
  onChange: (type: SurveyQuestion['type']) => void;
};

const QUESTION_TYPE_OPTIONS: Array<{
  type: SurveyQuestion['type'];
  title: string;
  description: string;
}> = [
  {
    type: 'Stars',
    title: 'Estrelas',
    description: 'CSAT clássico — avaliação visual de 1 a 5.',
  },
  {
    type: 'NumericScale',
    title: 'Escala numérica',
    description: 'NPS e notas — tipicamente 0 a 10.',
  },
  {
    type: 'LabeledScale',
    title: 'Régua com rótulos',
    description: 'Cada valor da escala tem um texto (ex.: Ruim → Excelente).',
  },
];

function StarsPreview() {
  return (
    <div className="flex items-center gap-0.5" aria-hidden="true">
      {[1, 2, 3, 4, 5].map((value) => (
        <StarIcon
          key={value}
          className={`h-3.5 w-3.5 ${value <= 4 ? 'text-amber-400' : 'text-secondary-200'}`}
        />
      ))}
    </div>
  );
}

function NumericPreview() {
  return (
    <div className="flex items-center gap-0.5" aria-hidden="true">
      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((value) => (
        <span
          key={value}
          className={`flex h-4 w-4 items-center justify-center rounded text-[9px] font-medium ${
            value === 9
              ? 'bg-primary-600 text-white'
              : 'bg-secondary-100 text-secondary-600'
          }`}
        >
          {value}
        </span>
      ))}
    </div>
  );
}

function LabeledPreview() {
  const labels = ['Ruim', 'Regular', 'Bom', 'Ótimo'];
  return (
    <div className="flex flex-wrap gap-1" aria-hidden="true">
      {labels.map((label, index) => (
        <span
          key={label}
          className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
            index === 2
              ? 'bg-primary-100 text-primary-800'
              : 'bg-secondary-100 text-secondary-600'
          }`}
        >
          {label}
        </span>
      ))}
    </div>
  );
}

function TypePreview({ type }: { type: SurveyQuestion['type'] }) {
  switch (type) {
    case 'Stars':
      return <StarsPreview />;
    case 'NumericScale':
      return <NumericPreview />;
    case 'LabeledScale':
      return <LabeledPreview />;
    default:
      return null;
  }
}

export function SurveyQuestionTypePicker({ value, onChange }: SurveyQuestionTypePickerProps) {
  return (
    <div className="grid gap-2 sm:grid-cols-3" role="radiogroup" aria-label="Tipo de pergunta">
      {QUESTION_TYPE_OPTIONS.map((option) => {
        const selected = value === option.type;
        return (
          <button
            key={option.type}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(option.type)}
            className={[
              'rounded-lg border px-3 py-2.5 text-left transition-colors',
              selected
                ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
                : 'border-secondary-200 bg-white hover:border-secondary-300 hover:bg-secondary-50',
            ].join(' ')}
          >
            <p className="text-sm font-medium text-secondary-900">{option.title}</p>
            <p className="mt-0.5 text-[11px] leading-snug text-secondary-500">
              {option.description}
            </p>
            <div className="mt-2 min-h-[1.25rem]">
              <TypePreview type={option.type} />
            </div>
          </button>
        );
      })}
    </div>
  );
}
